import { useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from '@srm/core/errors';
import { useConfirmLeave } from '../../hooks/useConfirmLeave';
import { useLatest } from '../../hooks/useLatest';
import { leaveAfterAccountChange } from '../AccountSwitch/accountSwitch';
import {
    sameAccount,
    type AccountId,
    type IAccountsFailure,
    type IAccountsPort,
    type IBrowserAccount,
} from '../AccountSwitch/accountsPort';
import type { IUserMenuAccountsProps } from './UserMenuAccounts';

export const LIST_FAILURE_TEXT = 'Не удалось загрузить учётные записи';
export const ACTION_FAILURE_TEXT = 'Не удалось выполнить действие с учётными записями';

export interface IUserMenuAccountsOptions {
    /** Меню открыто: список перечитывается при каждом открытии (учётную запись могли добавить в соседней вкладке) */
    opened: boolean;
    /** Мультиаккаунт в интерфейсе: флаг стенда, не PROD, `auth.accounts.enabled()` */
    enabled: boolean;
    /** Предел вместе с активной; 0 — сервер предел не назвал */
    maxAccounts: number;
    accounts: IAccountsPort;
    describeFailure: (error: unknown, maxAccounts: number) => IAccountsFailure;
    /** Страница входа «Добавить учётную запись» с возвратом на текущий адрес — в момент щелчка */
    addAccountUrl: () => string;
    /** Выход приложения: «Выйти из всех учётных записей» сначала убирает остальные из связки */
    onLogout?: () => void;
}

export interface IUserMenuAccounts {
    /** Пропсы `UserMenuAccounts`; `null` — мультиаккаунт выключен */
    accounts: IUserMenuAccountsProps | null;
    /** «Выйти из всех учётных записей»: есть, пока в связке есть другие */
    onLogoutAll?: () => void;
}

/**
 * Учётные записи браузера для меню профиля. Состояние живёт выше выпадающей панели (хук — в компоненте меню
 * приложения): закрытие меню не теряет ход и отказ, повторное открытие не запускает действие второй раз, прежний
 * список виден, пока грузится новый. Переключение и «Добавить» — уход со страницы (`useConfirmLeave`): черновик
 * досохраняется под прежней учётной записью, о прочих несохранённых правках — вопрос; затем страница уходит
 * (`leaveAfterAccountChange`).
 */
export function useUserMenuAccounts({
    opened,
    enabled,
    maxAccounts,
    accounts: port,
    describeFailure,
    addAccountUrl,
    onLogout,
}: IUserMenuAccountsOptions): IUserMenuAccounts {
    const confirmLeave = useConfirmLeave();
    const latest = useLatest({ port, describeFailure, maxAccounts, addAccountUrl, onLogout });
    const [list, setList] = useState<IBrowserAccount[] | null>(null);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState<string>();
    const [error, setError] = useState<string>();
    const [switchingId, setSwitchingId] = useState<AccountId | null>(null);
    const [removingId, setRemovingId] = useState<AccountId | null>(null);
    const [addLoading, setAddLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    // Синхронно, в отличие от состояния: двойной щелчок не запускает второе действие
    const pending = useRef(false);
    const request = useRef(0);

    const failureOf = useCallback(
        (failure: unknown, fallback = ACTION_FAILURE_TEXT) => {
            const described = latest.current.describeFailure(failure, latest.current.maxAccounts);
            return { code: described.code, text: described.message ?? errorText(failure, fallback) };
        },
        [latest],
    );

    useEffect(() => {
        if (!opened || !enabled) return;
        const current = ++request.current;
        setListLoading(true);
        latest.current.port.list().then(
            (next) => {
                if (current !== request.current) return;
                setList(next);
                setListError(undefined);
                setListLoading(false);
            },
            (failure: unknown) => {
                if (current !== request.current) return;
                setListError(failureOf(failure, LIST_FAILURE_TEXT).text);
                setListLoading(false);
            },
        );
    }, [opened, enabled, reloadKey, latest, failureOf]);

    const start = useCallback(() => {
        if (pending.current) return false;
        pending.current = true;
        setError(undefined);
        return true;
    }, []);

    const fail = useCallback(
        (failure: unknown, account?: IBrowserAccount) => {
            pending.current = false;
            const { code, text } = failureOf(failure);
            setError(text);
            if (!account) return;
            // Сервер оставил учётную запись протухшей или уже убрал её — меню показывает это без перезагрузки списка
            if (code === 'token_invalid') {
                setList(
                    (items) =>
                        items &&
                        items.map((item) =>
                            sameAccount(item.userId, account.userId) ? { ...item, expired: true } : item,
                        ),
                );
            } else if (code === 'account_not_found' || code === 'token_reuse') {
                setList((items) => items && items.filter((item) => !sameAccount(item.userId, account.userId)));
            }
        },
        [failureOf],
    );

    const add = useCallback(
        (loginHint?: string) => {
            if (pending.current) return;
            confirmLeave(() => {
                if (!start()) return;
                const url = latest.current.addAccountUrl();
                setAddLoading(true);
                latest.current.port.park(loginHint).then(
                    () => leaveAfterAccountChange(url),
                    (failure: unknown) => {
                        setAddLoading(false);
                        fail(failure);
                    },
                );
            });
        },
        [confirmLeave, start, fail, latest],
    );

    const select = useCallback(
        (account: IBrowserAccount) => {
            if (account.expired) {
                add(account.login);
                return;
            }
            if (pending.current) return;
            confirmLeave(() => {
                if (!start()) return;
                setSwitchingId(account.userId);
                latest.current.port.switchTo(account.userId).then(
                    () => leaveAfterAccountChange(),
                    (failure: unknown) => {
                        setSwitchingId(null);
                        fail(failure, account);
                    },
                );
            });
        },
        [add, confirmLeave, start, fail, latest],
    );

    const remove = useCallback(
        (account: IBrowserAccount) => {
            if (!start()) return;
            setRemovingId(account.userId);
            latest.current.port.remove(account.userId).then(
                () => {
                    pending.current = false;
                    setRemovingId(null);
                    setList((items) => items && items.filter((item) => !sameAccount(item.userId, account.userId)));
                },
                (failure: unknown) => {
                    setRemovingId(null);
                    fail(failure);
                },
            );
        },
        [start, fail, latest],
    );

    const logoutAll = useCallback(() => {
        if (!start()) return;
        latest.current.port.remove().then(
            () => {
                pending.current = false;
                setList([]);
                latest.current.onLogout?.();
            },
            (failure: unknown) => fail(failure),
        );
    }, [start, fail, latest]);

    if (!enabled) return { accounts: null };

    const items = list ?? [];
    const canAdd = maxAccounts <= 0 || items.length + 1 < maxAccounts;
    return {
        accounts: {
            accounts: items,
            listLoading,
            error: error ?? listError,
            switchingId,
            removingId,
            addLoading,
            onSelect: select,
            onRemove: remove,
            onAdd: canAdd ? () => add() : undefined,
            onRetry: listError && !listLoading ? () => setReloadKey((key) => key + 1) : undefined,
        },
        onLogoutAll: onLogout && items.length > 0 ? logoutAll : undefined,
    };
}
